import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usersService } from "@/services/usersService";
import { companyAPI } from "@/services/companyService";
import { contactoAPI } from "@/services/contactoService";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";
import { currencyAPI } from "@/services/currencyService";
import { opportunityAPI } from "@/services/opportunityService";

export const catalogKeys = {
  all: ["catalogs"] as const,
  users: (slug: string) => [...catalogKeys.all, "users", slug] as const,
  companies: (slug: string) => [...catalogKeys.all, "companies", slug] as const,
  contacts: (slug: string) => [...catalogKeys.all, "contacts", slug] as const,
  sources: (slug: string) => [...catalogKeys.all, "sources", slug] as const,
  currencies: (slug: string) => [...catalogKeys.all, "currencies", slug] as const,
  stages: (slug: string, pipelineId?: string) => [...catalogKeys.all, "stages", slug, pipelineId] as const,
};

export const fetchUsers = async (slug: string) => {
  const response = await usersService.getUsers(slug, 1, 1000, true);
  return response.data;
};

export const fetchCompanies = async (slug: string) => {
  const response = await companyAPI.getCompanies({ slug });
  return response.data;
};

export const fetchContacts = async (slug: string) => {
  const response = (await contactoAPI.getContactos(slug)) as any;
  return Array.isArray(response) ? response : (response?.data || []);
};

export const fetchSources = async (slug: string) => {
  const response = await opportunitySourcesAPI.getSources({ slug, is_active: true });
  return response.data;
};

export const fetchCurrencies = async (slug: string) => {
  const response = await currencyAPI.getCurrencies({ slug, is_active: true });
  return response.data;
};

export const fetchStages = async (slug: string, pipelineId?: string) => {
  const params: any = { slug, is_active: true };
  if (pipelineId) params.pipeline_id = pipelineId;
  const response = await opportunityAPI.getStages(params);
  return response.data;
};

export function useGetUsers(slug: string) {
  return useQuery({
    queryKey: catalogKeys.users(slug),
    queryFn: () => fetchUsers(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 30, // 30 minutos
  });
}

export function useGetCompanies(slug: string) {
  return useQuery({
    queryKey: catalogKeys.companies(slug),
    queryFn: () => fetchCompanies(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 30,
  });
}

export function useGetContacts(slug: string) {
  return useQuery({
    queryKey: catalogKeys.contacts(slug),
    queryFn: () => fetchContacts(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 30,
  });
}

export function useGetSources(slug: string) {
  return useQuery({
    queryKey: catalogKeys.sources(slug),
    queryFn: () => fetchSources(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 30,
  });
}

export function useGetCurrencies(slug: string) {
  return useQuery({
    queryKey: catalogKeys.currencies(slug),
    queryFn: () => fetchCurrencies(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 60, // 1 hora
  });
}

export function useGetStages(slug: string, pipelineId?: string) {
  return useQuery({
    queryKey: catalogKeys.stages(slug, pipelineId),
    queryFn: () => fetchStages(slug, pipelineId),
    enabled: !!slug,
    staleTime: 1000 * 60 * 30,
  });
}

export function usePreloadCatalogs(slug: string) {
  const queryClient = useQueryClient();

  const preload = async () => {
    if (!slug) return;

    // Trigger prefetch for all common catalogs
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: catalogKeys.users(slug),
        queryFn: () => fetchUsers(slug),
        staleTime: 1000 * 60 * 30,
      }),
      queryClient.prefetchQuery({
        queryKey: catalogKeys.companies(slug),
        queryFn: () => fetchCompanies(slug),
        staleTime: 1000 * 60 * 30,
      }),
      queryClient.prefetchQuery({
        queryKey: catalogKeys.contacts(slug),
        queryFn: () => fetchContacts(slug),
        staleTime: 1000 * 60 * 30,
      }),
      queryClient.prefetchQuery({
        queryKey: catalogKeys.sources(slug),
        queryFn: () => fetchSources(slug),
        staleTime: 1000 * 60 * 30,
      }),
      queryClient.prefetchQuery({
        queryKey: catalogKeys.currencies(slug),
        queryFn: () => fetchCurrencies(slug),
        staleTime: 1000 * 60 * 60,
      }),
      queryClient.prefetchQuery({
        queryKey: catalogKeys.stages(slug, undefined),
        queryFn: () => fetchStages(slug),
        staleTime: 1000 * 60 * 30,
      }),
    ]);
  };

  return { preload };
}
